import { useComputed, useSignal } from "@preact/signals";
import { useId } from "preact/hooks";
import { IcoCheckSquare, IcoSearch, IcoSquare, IcoUser } from "../icons";

export type MemberOption = {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: "admin" | "member";
};

type MemberPickerProps = {
  members: MemberOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentMemberId?: string;
  placeholder?: string;
};

const MAX_VISIBLE = 50;

export function MemberPicker({
  members,
  selectedIds,
  onChange,
  currentMemberId,
  placeholder = "Search members...",
}: MemberPickerProps) {
  const listboxId = useId();
  const search = useSignal("");

  const selectedSet = new Set(selectedIds);

  const filteredMembers = useComputed(() => {
    const query = search.value.toLowerCase().trim();
    let filtered = members;

    if (query) {
      filtered = members.filter(
        (m) =>
          m.userName.toLowerCase().includes(query) ||
          m.userEmail.toLowerCase().includes(query),
      );
    }

    return filtered.slice(0, MAX_VISIBLE);
  });

  const toggleMember = (memberId: string) => {
    if (selectedSet.has(memberId)) {
      onChange(selectedIds.filter((id) => id !== memberId));
    } else {
      onChange([...selectedIds, memberId]);
    }
  };

  const selectAll = () => {
    const allIds = members.map((m) => m.id);
    onChange(allIds);
  };

  const deselectAll = () => {
    onChange([]);
  };

  const allSelected =
    members.length > 0 && selectedIds.length === members.length;

  return (
    <div class="flex flex-col gap-3">
      {/* Search input */}
      <div class="relative">
        <IcoSearch class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-subtle" />
        <input
          type="text"
          placeholder={placeholder}
          value={search.value}
          onInput={(e) => {
            search.value = (e.target as HTMLInputElement).value;
          }}
          aria-controls={listboxId}
          class="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-surface text-text placeholder:text-text-subtle hover:border-text-subtle focus:ring-ring transition-colors"
        />
      </div>

      {/* Select all / Deselect all */}
      <div class="flex items-center justify-between text-sm">
        <span class="text-text-muted">
          {selectedIds.length} of {members.length} selected
        </span>
        <div class="flex gap-2">
          <button
            type="button"
            onClick={selectAll}
            disabled={allSelected}
            class="text-text-muted hover:text-text disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Select all
          </button>
          <span class="text-text-subtle">|</span>
          <button
            type="button"
            onClick={deselectAll}
            disabled={selectedIds.length === 0}
            class="text-text-muted hover:text-text disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Deselect all
          </button>
        </div>
      </div>

      {/* Member list */}
      <div
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        class="rounded-lg border border-border bg-surface max-h-64 overflow-y-auto"
      >
        {filteredMembers.value.length === 0 ? (
          <div class="px-3 py-4 text-center text-text-muted">
            No members found
          </div>
        ) : (
          filteredMembers.value.map((member) => {
            const isSelected = selectedSet.has(member.id);
            const isCurrentUser = member.id === currentMemberId;

            return (
              <button
                key={member.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => toggleMember(member.id)}
                class="w-full flex items-center gap-3 px-3 py-2 hover:bg-surface-hover transition-colors text-left"
              >
                {isSelected ? (
                  <IcoCheckSquare class="size-5 text-text flex-shrink-0" />
                ) : (
                  <IcoSquare class="size-5 text-text-muted flex-shrink-0" />
                )}
                <div class="size-8 rounded-full flex items-center justify-center bg-surface-muted flex-shrink-0">
                  <IcoUser class="size-4 text-text-muted" />
                </div>
                <div class="flex flex-col min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-text truncate">
                      {member.userName || member.userEmail}
                    </span>
                    {isCurrentUser && (
                      <span class="text-xs text-text-muted">(you)</span>
                    )}
                    {member.role === "admin" && (
                      <span class="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                        Admin
                      </span>
                    )}
                  </div>
                  {member.userName && (
                    <span class="text-sm text-text-muted truncate">
                      {member.userEmail}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {filteredMembers.value.length === MAX_VISIBLE &&
        members.length > MAX_VISIBLE && (
          <p class="text-xs text-text-muted text-center">
            Showing first {MAX_VISIBLE} members. Use search to find more.
          </p>
        )}
    </div>
  );
}
